import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useConcello, ConcelloLogo } from "@/lib/concellos";
import { EVENT_TYPES, KINDS, RESOURCES, CATEGORY } from "@/lib/constants";
import EventMap, { NoEntryMini } from "@/components/map/EventMap";
import { SIGNS, SignMini } from "@/components/map/SignObjects";
import { UNIT_LEGEND, UnitMini } from "@/components/map/StaffMarkers";
import { estado, AVISO_DESVIOS, AVISO_DESFASADO } from "@/lib/estadosDesvio";
import { mandosDe, unidadCompleta, nombresDe } from "@/lib/staff";
import { useAuth } from "@/context/AuthContext";
import { useDocumentos, editable, BarraEdicion, avisoError } from "@/components/DocumentoEditable";
import { toast } from "sonner";

const H = ({ children }) => <h2 className="font-display text-lg font-bold text-slate-900 border-b-2 border-blue-700 pb-1 mt-8 mb-3">{children}</h2>;
const T = ({ head, rows, testid }) => (
  <table className="w-full text-[11px] border-collapse" data-testid={testid}>
    <thead><tr>{head.map((h) => <th key={h} className="text-left bg-slate-200 text-slate-900 px-2 py-1.5 border border-slate-300">{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="px-2 py-1 border border-slate-300 text-slate-800 align-top">{c}</td>)}</tr>)}</tbody>
  </table>
);

export default function PrintPlan() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  useEffect(() => { api.get(`/events/${id}`).then((r) => setEv(r.data)); }, [id]);
  const concello = useConcello(ev?.concello);
  // edición a mano del documento: se guarda aparte y se muestra en lugar del generado
  const { canEdit } = useAuth();
  const { docs, guardar, quitar } = useDocumentos(id);
  const pagina = useRef(null);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [ver, setVer] = useState(0);
  const onGuardar = async () => {
    setGuardando(true);
    try {
      editable(pagina.current, false);
      await guardar("plan", { html: pagina.current.innerHTML });
      setEditando(false); setVer((v) => v + 1);
      toast.success("Plan guardado con sus cambios");
    } catch (e) { editable(pagina.current, true); avisoError(e); } finally { setGuardando(false); }
  };
  const onCancelar = () => { editable(pagina.current, false); setEditando(false); setVer((v) => v + 1); };
  const onRestaurar = async () => {
    if (!window.confirm("Se perderán los cambios hechos a mano y se mostrará el plan generado. ¿Continuar?")) return;
    try { await quitar("plan"); setVer((v) => v + 1); } catch (e) { avisoError(e); }
  };
  if (!ev) return <div className="p-10 text-slate-500">Cargando…</div>;
  const p = ev.plan;
  const nombres = nombresDe(ev);
  if (!p) return <div className="p-10 bg-white min-h-screen text-slate-800">Este evento aún no tiene plan de tráfico generado.</div>;

  return (
    <div className="bg-slate-100 min-h-screen">
      <div className="no-print sticky top-0 z-[1000] bg-[#0b1329] text-white px-6 py-3 flex items-center justify-between">
        <span className="text-sm">Vista de impresión · elija “Guardar como PDF” en el diálogo de impresión</span>
        <button onClick={() => window.print()} disabled={editando} className="flex items-center gap-2 bg-sky-500 text-slate-950 font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-50" data-testid="print-pdf-button">
          <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
        </button>
      </div>
      {/* aviso del momento (no se guarda con el documento editado) */}
      {ev.plan_vigente === false && <div className="max-w-[900px] mx-auto mt-4 border-2 border-red-600 bg-red-50 text-red-800 text-sm font-semibold rounded-lg px-4 py-3" data-testid="plan-stale-print">{AVISO_DESFASADO}</div>}
      <BarraEdicion canEdit={canEdit} editando={editando} guardando={guardando} editados={docs.plan ? [docs.plan] : []} huella={p.escenario?.huella}
        onEditar={() => { editable(pagina.current, true); setEditando(true); }} onGuardar={onGuardar} onCancelar={onCancelar} onRestaurar={onRestaurar} />
      {docs.plan ? (
        <div key={`e${ver}`} ref={pagina} data-editable className={`print-page max-w-[900px] mx-auto bg-white p-10 text-slate-900 mt-4 ${editando ? "outline outline-2 outline-amber-400" : ""}`} data-testid="print-plan-page"
          dangerouslySetInnerHTML={{ __html: docs.plan.html }} />
      ) : (
      <div key={`g${ver}`} ref={pagina} data-editable className={`print-page max-w-[900px] mx-auto bg-white p-10 text-slate-900 mt-4 ${editando ? "outline outline-2 outline-amber-400" : ""}`} data-testid="print-plan-page">
        <div className="flex justify-between items-start gap-4 border-b-4 border-blue-800 pb-4">
          <ConcelloLogo ine={ev.concello} className="h-20 w-auto max-w-[7rem] shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] font-mono text-blue-800">{concello || ev.town?.name ? `AYUNTAMIENTO DE ${(concello?.nombre || ev.town.name).toUpperCase()} · POLICÍA LOCAL` : "POLICÍAS DE GALICIA"}</div>
            <h1 className="font-display text-3xl font-extrabold mt-1">Plan de tráfico</h1>
            <div className="text-lg font-semibold">{ev.name}</div>
          </div>
          <div className="text-right text-xs font-mono">
            <div>{EVENT_TYPES[ev.type]}</div><div>{ev.date}</div><div>{ev.start_time} – {ev.end_time}</div>
            <div className="text-slate-500 mt-1">Generado {new Date(p.generated_at).toLocaleDateString("es-ES")}</div>
          </div>
        </div>
        {ev.description && <p className="text-sm mt-3 text-slate-700">{ev.description}</p>}

        <div className="h-[380px] mt-5 border border-slate-300 rounded overflow-hidden">
          <EventMap elements={ev.elements.filter((e) => !(e.auto_generated && !e.locked))} detours={p.detours.filter((d) => !d.pending)} timings={p.timings} light interactive={false} fitTrigger={1} staff={p.staff_points} />
        </div>
        <div className="flex flex-wrap gap-4 text-[10px] mt-2 text-slate-700">
          {Object.entries(SIGNS).map(([k, v]) => <span key={k} className="flex items-center gap-1"><SignMini type={k} size={11} />{v.label}</span>)}
          {UNIT_LEGEND.map(([k, l]) => <span key={k} className="flex items-center gap-1"><UnitMini type={k} size={13} />{l}</span>)}
          {Object.entries(KINDS).filter(([k]) => k !== "sign").map(([k, v]) => <span key={k} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />{v.label}</span>)}
          <span className="flex items-center gap-1"><NoEntryMini />Corte previo (automático)</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-600" />Desvío propuesto</span>
        </div>

        <H>1. Resumen</H>
        {mandosDe(ev).length > 0 && <div className="text-sm mb-3" data-testid="print-mandos"><b>Mando del dispositivo:</b> {mandosDe(ev).join(" · ")}</div>}
        <T testid="print-summary-table" head={["Efectivos usados", "Sin reaprovechar", "Traslados", "Relevos de turno", "Pico policial", "Déficit"]}
          rows={[[p.summary.agents_used, p.summary.agents_without_reuse, p.summary.relocations, p.summary.shift_handovers ?? 0, p.summary.peak_police_need, p.summary.missing_agents]]} />
        <div className="mt-3"><T head={["Recurso", "Disponible", "En plan"]} rows={RESOURCES.map((r) => [r.label, ev.resources[r.key] ?? 0, p.summary.by_type.find((b) => b.type === r.key)?.used ?? 0])} /></div>
        {ev.shifts?.length > 0 && <div className="mt-3"><T testid="print-shifts-table" head={["Grupo", "Nº", "Entrada", "Salida", "Nota"]} rows={ev.shifts.map((s) => [RESOURCES.find((r) => r.key === s.type)?.label, s.count, s.start, s.end, s.label || ""])} /></div>}

        <H>2. Cortes, tramos y zonas</H>
        <T head={["Tipo", "Nombre", "Desde", "Hasta", "Origen horario"]}
          rows={ev.elements.filter((e) => p.timings[e.id]).map((e) => [KINDS[e.kind].short, e.name, p.timings[e.id].start_hm, p.timings[e.id].end_hm, p.timings[e.id].source])} />

        <H>3. Desvíos</H>
        <p className="text-[0.6875rem] italic text-slate-600 mb-1.5">{AVISO_DESVIOS}</p>
        <T head={["Corte", "Estado", "Itinerario alternativo", "Distancia"]}
          rows={p.detours.filter((d) => !d.pending).map((d) => [d.element_name, estado(d.status).short, (d.from ? `Desde ${d.from}: ` : "") + (d.streets.join(" → ") || "—") + (d.branches || []).map((b) => ` · Desde ${b.from}: ${b.streets.join(" → ")}`).join("") + (d.special_name ? ` [zona especial: ${d.special_name}]` : "") + (d.reversed_name ? ` [sentido invertido: ${d.reversed_name}]` : "") + (d.oneway_conflicts?.length ? ` [CONTRA SENTIDO: ${d.oneway_conflicts.map((c) => c.name).join(", ")}]` : ""), d.distance_m ? `${d.distance_m} m` : "—"])} />

        <H>4. Despliegue de personal y relevos</H>
        <T head={["Unidad", "Horario", "Tarea", "Desplazamiento"]}
          rows={p.units.filter((u) => u.segments.length).flatMap((u) => u.segments.map((s, i) => [i === 0 ? `${((n) => `${unidadCompleta(n.tipo, n.nombre)}${u.tip && !n.nombre.startsWith("TIP") ? ` · TIP ${u.tip}` : ""}`)(nombres[u.id] || { tipo: u.tipo, nombre: u.nombre || u.id })}${u.shift_hm ? ` (turno ${u.shift_hm})` : ""}` : "", `${s.start_hm}–${s.end_hm}`, `${CATEGORY[s.category]?.label}: ${s.name}${s.handover ? " [RELEVO]" : ""}`, s.depart_hm ? `Sale ${s.depart_hm} desde ${s.from} (${s.travel_min} min)` : "Desde base"]))} />
        {p.deficits.length > 0 && (
          <div className="mt-3"><T head={["Puesto sin cubrir", "Horario", "Faltan"]} rows={p.deficits.map((d) => [d.name, `${d.start}–${d.end}`, d.missing])} /></div>
        )}

        <H>5. Señalización y estacionamiento</H>
        <T head={["Código", "Elemento", "Cantidad", "Ubicación"]} rows={p.signage.map((s) => [s.code, s.name, s.qty, s.where.join(", ")])} />
        {p.parking.length > 0 && <div className="mt-3"><T head={["Zona", "Prohibido", "Vehículos est."]} rows={p.parking.map((z) => [z.name, `${z.from} – ${z.to}`, `~${z.est_vehicles}`])} /></div>}

        {(docs.orden?.texto || p.ai_report) && (
          <div className="print-break">
            <H>6. Orden de servicio</H>
            <div className="report-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{docs.orden?.texto || p.ai_report}</ReactMarkdown></div>
          </div>
        )}
        <div className="mt-12 grid grid-cols-2 gap-10 text-xs text-slate-600">
          <div className="border-t border-slate-400 pt-2">Fdo.: El/La Jefe/a de la Policía Local</div>
          <div className="border-t border-slate-400 pt-2">Vº Bº: Concejalía de Seguridad Ciudadana</div>
        </div>
      </div>
      )}
    </div>
  );
}
